import { useState } from 'react';
import { Select } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { useBreakpoint } from '@/shared/lib/hooks';
import type { ReportWindowType } from '@/shared/lib/windows';
import {
  DebtsReportPage,
  ExpiryReportPage,
  NegativeStockReportPage,
  ProfitReportPage,
  ProvisionalSalesReportPage,
  SalesProfitReportPage,
  SalesReportPage,
  StockReportPage,
  TurnoverReportPage,
  WriteOffsReportPage,
} from '@/features/reports';

const REPORT_LABELS: Record<ReportWindowType, string> = {
  stock: 'Остатки',
  turnover: 'Обороты',
  sales: 'Продажи',
  profit: 'Финансовые отчёты',
  'sales-profit': 'Прибыль по продажам',
  'write-offs': 'Списания',
  debts: 'Долги',
  expiry: 'Сроки годности',
  'negative-stock': 'Товары в минусе',
  'provisional-sales': 'Предварительная себестоимость',
};

const REPORT_OPTIONS = (Object.keys(REPORT_LABELS) as ReportWindowType[]).map((value) => ({ value, label: REPORT_LABELS[value] }));

export interface ReportWindowProps {
  report: ReportWindowType;
  onClose: () => void;
}

/** §13, §1.8: каждый отчёт — своё плавающее окно (как документы/оплаты, §11.1): можно открыть
 *  сразу несколько разных отчётов, каждый двигается и запоминает размер/позицию независимо.
 *
 *  Телефон: окно — лист на весь экран; сверху выпадающий список «Отчёт» — внутри листа можно
 *  переключиться на любой из 10 отчётов, не возвращаясь в меню. */
export function ReportWindow({ report, onClose }: ReportWindowProps) {
  const { isMobile } = useBreakpoint();
  // На десктопе `current` всегда равен `report` (переключателя там нет); окно по-прежнему
  // определяется исходным отчётом (`kind`), а меняется только то, что внутри.
  const [current, setCurrent] = useState<ReportWindowType>(report);
  const content = (() => {
    switch (current) {
      case 'stock':
        return <StockReportPage />;
      case 'turnover':
        return <TurnoverReportPage />;
      case 'sales':
        return <SalesReportPage />;
      case 'profit':
        return <ProfitReportPage />;
      case 'sales-profit':
        return <SalesProfitReportPage />;
      case 'write-offs':
        return <WriteOffsReportPage />;
      case 'debts':
        return <DebtsReportPage />;
      case 'expiry':
        return <ExpiryReportPage />;
      case 'negative-stock':
        return <NegativeStockReportPage />;
      case 'provisional-sales':
        return <ProvisionalSalesReportPage />;
    }
  })();

  return (
    <FloatingWindow kind={`report:${report}`} title={REPORT_LABELS[current]} onClose={onClose}>
      {isMobile ? (
        <Select<ReportWindowType>
          size="large"
          value={current}
          options={REPORT_OPTIONS}
          onChange={setCurrent}
          aria-label="Отчёт"
          style={{ width: '100%', marginBottom: 12 }}
        />
      ) : null}
      {content}
    </FloatingWindow>
  );
}
